import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { 
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell, Legend
} from 'recharts';
import { 
  Store, ShoppingCart, Users, TrendingUp, TrendingDown, IndianRupee,
  AlertCircle, Clock, Package, ChevronRight, ArrowUpRight, ArrowDownRight,
  Calendar as CalendarIcon, Coffee, Wallet, Activity, Target, BarChart2, PieChart as PieChartIcon, LineChart
} from 'lucide-react';
import { format, subDays, startOfDay, endOfDay, isWithinInterval, parseISO } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Date Range Picker Component
const DateRangePicker = ({ dateRange, onDateRangeChange, label }) => {
  const [open, setOpen] = useState(false);
  
  const presets = [
    { label: 'Today', days: 0 },
    { label: 'Last 7 days', days: 7 },
    { label: 'Last 30 days', days: 30 },
    { label: 'Last 90 days', days: 90 },
  ];
  
  const handlePreset = (days) => {
    const end = new Date();
    const start = days === 0 ? new Date() : subDays(new Date(), days);
    onDateRangeChange({ from: start, to: end });
    setOpen(false);
  };
  
  const handleSelect = (range) => {
    if (range?.from) {
      onDateRangeChange(range);
      if (range.to) {
        setOpen(false);
      }
    }
  };
  
  const formatDateRange = () => {
    if (!dateRange?.from) return 'Select dates';
    if (!dateRange?.to) return format(dateRange.from, 'MMM d, yyyy');
    if (format(dateRange.from, 'yyyy-MM-dd') === format(dateRange.to, 'yyyy-MM-dd')) {
      return format(dateRange.from, 'MMM d, yyyy');
    }
    return `${format(dateRange.from, 'MMM d')} - ${format(dateRange.to, 'MMM d, yyyy')}`;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="outline" 
          size="sm" 
          className="h-8 text-xs gap-1.5 bg-white border-stone-200 text-stone-600 hover:bg-stone-50"
          data-testid={`date-picker-${label?.toLowerCase().replace(/\s/g, '-')}`}
        >
          <CalendarIcon className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">{formatDateRange()}</span>
          <span className="sm:hidden">{dateRange?.from ? format(dateRange.from, 'MM/dd') : 'Date'}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-auto p-0 bg-white border border-stone-200 shadow-xl z-[100]" 
        align="end"
        sideOffset={5}
        style={{ backgroundColor: 'white', opacity: 1 }}
      >
        <div className="flex bg-white rounded-lg">
          <div className="border-r border-stone-200 p-2 space-y-1 bg-stone-50">
            <p className="text-xs font-medium text-stone-500 px-2 py-1">Quick Select</p>
            {presets.map((preset) => (
              <Button
                key={preset.label}
                variant="ghost"
                size="sm"
                className="w-full justify-start text-xs h-8 hover:bg-white"
                onClick={() => handlePreset(preset.days)}
              >
                {preset.label}
              </Button>
            ))}
          </div>
          <div className="p-2 bg-white">
            <Calendar
              mode="range"
              selected={dateRange}
              onSelect={handleSelect}
              numberOfMonths={1}
              disabled={(date) => date > new Date()}
              className="rounded-md bg-white"
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

// Chart Type Selector Component
const ChartTypeSelector = ({ chartType, onChartTypeChange }) => {
  const types = [
    { type: 'area', icon: LineChart, label: 'Area' },
    { type: 'bar', icon: BarChart2, label: 'Bar' },
    { type: 'pie', icon: PieChartIcon, label: 'Pie' },
  ];
  
  return (
    <div className="flex gap-1 bg-stone-100 rounded-lg p-1">
      {types.map(({ type, icon: Icon, label }) => (
        <Button
          key={type}
          variant={chartType === type ? 'default' : 'ghost'}
          size="sm"
          className={`h-7 px-2 text-xs ${chartType === type ? 'bg-white shadow-sm' : 'hover:bg-stone-200'}`}
          onClick={() => onChartTypeChange(type)}
          data-testid={`chart-type-${type}`}
        >
          <Icon className="w-3.5 h-3.5 mr-1" />
          <span className="hidden sm:inline">{label}</span>
        </Button>
      ))}
    </div>
  );
};

const DashboardOverview = ({ user, franchises: propFranchises, onNavigate }) => {
  const [loading, setLoading] = useState(true);
  const [franchises, setFranchises] = useState(propFranchises || []);
  const [allOrders, setAllOrders] = useState([]);
  
  // Date ranges for each section
  const [revenueDateRange, setRevenueDateRange] = useState({ from: subDays(new Date(), 7), to: new Date() });
  const [topItemsDateRange, setTopItemsDateRange] = useState({ from: subDays(new Date(), 30), to: new Date() });
  const [franchiseDateRange, setFranchiseDateRange] = useState({ from: subDays(new Date(), 30), to: new Date() });
  const [recentOrdersDateRange, setRecentOrdersDateRange] = useState({ from: subDays(new Date(), 7), to: new Date() });
  
  // Chart type for revenue trend
  const [revenueChartType, setRevenueChartType] = useState('area');
  
  const [dashboardData, setDashboardData] = useState({
    todayStats: { orders: 0, revenue: 0, avgOrderValue: 0 },
    pendingActions: []
  });
  
  // Filtered data based on date ranges
  const [filteredRevenueData, setFilteredRevenueData] = useState([]);
  const [filteredTopItems, setFilteredTopItems] = useState([]);
  const [filteredFranchisePerformance, setFilteredFranchisePerformance] = useState([]);
  const [filteredRecentOrders, setFilteredRecentOrders] = useState([]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('authToken') || localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  const getDateString = (date) => {
    if (!date) return '';
    try {
      return new Date(date).toISOString().split('T')[0];
    } catch (e) {
      return '';
    }
  };

  const isOrderInDateRange = useCallback((order, dateRange) => {
    if (!dateRange?.from || !order) return false;
    try {
      const orderDate = new Date(order.date_timestamp || order.created_at);
      const from = startOfDay(dateRange.from);
      const to = endOfDay(dateRange.to || dateRange.from);
      return isWithinInterval(orderDate, { start: from, end: to });
    } catch (e) {
      return false;
    }
  }, []);

  // Calculate revenue data based on date range
  const calculateRevenueData = useCallback((orders, dateRange) => {
    if (!dateRange?.from || !dateRange?.to) return [];
    
    const days = Math.ceil((dateRange.to - dateRange.from) / (1000 * 60 * 60 * 24)) + 1;
    const revenueData = [];
    
    for (let i = days - 1; i >= 0; i--) {
      const date = subDays(dateRange.to, i);
      const dateStr = getDateString(date);
      
      const dayOrders = orders.filter(order => {
        const orderDateStr = getDateString(order.date_timestamp || order.created_at);
        return orderDateStr === dateStr;
      });
      
      const dayRevenue = dayOrders.reduce((sum, order) => {
        return sum + parseFloat(order.total_value || order.total_amount || 0);
      }, 0);
      
      revenueData.push({
        day: days <= 7 ? format(date, 'EEE') : format(date, 'MM/dd'),
        date: format(date, 'MMM d, yyyy'),
        fullDate: dateStr,
        revenue: dayRevenue,
        orders: dayOrders.length
      });
    }
    
    return revenueData;
  }, []);

  // Calculate top items based on date range
  const calculateTopItems = useCallback((orders, dateRange) => {
    const filteredOrders = orders.filter(o => isOrderInDateRange(o, dateRange));
    const itemCounts = {};
    
    filteredOrders.forEach(order => {
      try {
        const details = typeof order.order_details_json === 'string' 
          ? JSON.parse(order.order_details_json) 
          : order.order_details;
        
        if (details?.items) {
          details.items.forEach(item => {
            const name = item.name || item.item_name;
            if (name) {
              if (!itemCounts[name]) {
                itemCounts[name] = { name, count: 0, revenue: 0 };
              }
              itemCounts[name].count += item.quantity || 1;
              itemCounts[name].revenue += (item.price || 0) * (item.quantity || 1);
            }
          });
        }
      } catch (e) {}
    });
    
    return Object.values(itemCounts)
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  }, [isOrderInDateRange]);

  // Calculate franchise performance based on date range
  const calculateFranchisePerformance = useCallback((orders, franchisesList, dateRange) => {
    const filteredOrders = orders.filter(o => isOrderInDateRange(o, dateRange));
    
    return franchisesList.map(franchise => {
      const fId = franchise.id || franchise.franchise_id;
      const franchiseOrders = filteredOrders.filter(o => o.franchise_id === fId);
      const revenue = franchiseOrders.reduce((sum, o) => 
        sum + parseFloat(o.total_value || o.total_amount || 0), 0
      );
      
      // Calculate trend based on the selected period
      const periodDays = Math.ceil((dateRange.to - dateRange.from) / (1000 * 60 * 60 * 24)) + 1;
      const midPoint = subDays(dateRange.to, Math.floor(periodDays / 2));
      
      const firstHalfRevenue = franchiseOrders
        .filter(o => new Date(o.date_timestamp || o.created_at) <= midPoint)
        .reduce((sum, o) => sum + parseFloat(o.total_value || o.total_amount || 0), 0);
      
      const secondHalfRevenue = franchiseOrders
        .filter(o => new Date(o.date_timestamp || o.created_at) > midPoint)
        .reduce((sum, o) => sum + parseFloat(o.total_value || o.total_amount || 0), 0);
      
      const trendValue = firstHalfRevenue > 0 
        ? Math.round(((secondHalfRevenue - firstHalfRevenue) / firstHalfRevenue) * 100)
        : (secondHalfRevenue > 0 ? 100 : 0);
      
      return {
        id: fId,
        name: franchise.name || franchise.franchise_name || 'Unknown',
        revenue,
        orders: franchiseOrders.length,
        trend: trendValue >= 0 ? 'up' : 'down',
        trendValue: Math.abs(trendValue)
      };
    }).sort((a, b) => b.revenue - a.revenue);
  }, [isOrderInDateRange]);

  // Calculate recent orders based on date range
  const calculateRecentOrders = useCallback((orders, dateRange) => {
    const filteredOrders = orders.filter(o => isOrderInDateRange(o, dateRange));
    
    return filteredOrders
      .sort((a, b) => new Date(b.date_timestamp || b.created_at) - new Date(a.date_timestamp || a.created_at))
      .slice(0, 10)
      .map(order => ({
        id: order.order_no || order.order_id,
        franchise: order.franchise_name || 'Unknown',
        amount: parseFloat(order.total_value || order.total_amount || 0),
        time: formatTimeAgo(new Date(order.date_timestamp || order.created_at)),
        date: format(new Date(order.date_timestamp || order.created_at), 'MMM d, h:mm a'),
        status: 'completed'
      }));
  }, [isOrderInDateRange]);

  // Update filtered data when date ranges change
  useEffect(() => {
    if (allOrders.length > 0) {
      setFilteredRevenueData(calculateRevenueData(allOrders, revenueDateRange));
    }
  }, [allOrders, revenueDateRange, calculateRevenueData]);

  useEffect(() => {
    if (allOrders.length > 0) {
      setFilteredTopItems(calculateTopItems(allOrders, topItemsDateRange));
    }
  }, [allOrders, topItemsDateRange, calculateTopItems]);

  useEffect(() => {
    if (allOrders.length > 0 && franchises.length > 0) {
      setFilteredFranchisePerformance(calculateFranchisePerformance(allOrders, franchises, franchiseDateRange));
    }
  }, [allOrders, franchises, franchiseDateRange, calculateFranchisePerformance]);

  useEffect(() => {
    if (allOrders.length > 0) {
      setFilteredRecentOrders(calculateRecentOrders(allOrders, recentOrdersDateRange));
    }
  }, [allOrders, recentOrdersDateRange, calculateRecentOrders]);

  useEffect(() => {
    fetchDashboardData();
  }, []);
  
  useEffect(() => {
    if (propFranchises && propFranchises.length > 0) {
      setFranchises(propFranchises);
    }
  }, [propFranchises]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      const todayStr = new Date().toISOString().split('T')[0];
      
      // Fetch franchises
      const franchisesResponse = await fetch(`${API_URL}/api/franchises`, {
        headers: getAuthHeaders()
      });
      const franchisesData = await franchisesResponse.json();
      const fetchedFranchises = franchisesData.franchises || [];
      setFranchises(fetchedFranchises);
      
      // Fetch orders (last 90 days for comprehensive filtering)
      const ninetyDaysAgo = new Date();
      ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
      const startDate = ninetyDaysAgo.toISOString().split('T')[0];
      
      const ordersResponse = await fetch(
        `${API_URL}/api/orders?start_date=${startDate}&limit=10000`, 
        { headers: getAuthHeaders() }
      );
      const ordersData = await ordersResponse.json();
      const orders = ordersData.orders || [];
      setAllOrders(orders);
      
      // Calculate today's stats
      const todayOrders = orders.filter(order => {
        const orderDateStr = getDateString(order.date_timestamp || order.created_at);
        return orderDateStr === todayStr;
      });
      
      const todayRevenue = todayOrders.reduce((sum, order) => {
        return sum + parseFloat(order.total_value || order.total_amount || 0);
      }, 0);
      
      // Pending actions
      const pendingActions = [];
      if (fetchedFranchises.length === 0) {
        pendingActions.push({
          type: 'setup',
          title: 'Add Your First Franchise',
          description: 'Get started by adding a franchise location',
          action: 'franchises'
        });
      }
      
      setDashboardData({
        todayStats: {
          orders: todayOrders.length,
          revenue: todayRevenue,
          avgOrderValue: todayOrders.length > 0 ? todayRevenue / todayOrders.length : 0
        },
        pendingActions,
        totalOrders: orders.length,
        totalRevenue: orders.reduce((sum, o) => sum + parseFloat(o.total_value || o.total_amount || 0), 0)
      });
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTimeAgo = (date) => {
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const CHART_COLORS = ['#D97706', '#F59E0B', '#10B981', '#3B82F6', '#6366F1', '#8B5CF6', '#EC4899'];

  // Calculate totals for filtered data
  const filteredRevenueTotals = filteredRevenueData.reduce(
    (acc, day) => ({ revenue: acc.revenue + day.revenue, orders: acc.orders + day.orders }),
    { revenue: 0, orders: 0 }
  );

  // Render Revenue Chart based on type
  const renderRevenueChart = () => {
    if (revenueChartType === 'pie') {
      const pieData = filteredRevenueData
        .filter(d => d.revenue > 0)
        .map(d => ({ name: d.day, value: d.revenue, date: d.date }));
      
      if (pieData.length === 0) {
        return (
          <div className="h-[280px] flex items-center justify-center text-stone-400">
            No revenue data for selected period
          </div>
        );
      }
      
      return (
        <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={200}>
          <PieChart>
            <Pie
              data={pieData}
              cx="50%"
              cy="50%"
              innerRadius={60}
              outerRadius={100}
              paddingAngle={2}
              dataKey="value"
              label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
              labelLine={false}
            >
              {pieData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
              ))}
            </Pie>
            <Tooltip 
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  return (
                    <div className="bg-white p-3 rounded-lg shadow-lg border border-stone-200">
                      <p className="text-sm font-medium text-stone-900">{payload[0].payload.date}</p>
                      <p className="text-lg font-bold text-amber-600">{formatCurrency(payload[0].value)}</p>
                    </div>
                  );
                }
                return null;
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      );
    }
    
    if (revenueChartType === 'bar') {
      return (
        <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={200}>
          <BarChart data={filteredRevenueData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
            <XAxis dataKey="day" stroke="#78716C" fontSize={12} tickLine={false} />
            <YAxis 
              stroke="#78716C" 
              fontSize={12}
              tickLine={false}
              tickFormatter={(value) => `₹${value >= 1000 ? `${(value/1000).toFixed(0)}k` : value}`}
            />
            <Tooltip 
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  return (
                    <div className="bg-white p-3 rounded-lg shadow-lg border border-stone-200">
                      <p className="text-sm font-medium text-stone-900">{payload[0].payload.date}</p>
                      <p className="text-lg font-bold text-amber-600">{formatCurrency(payload[0].value)}</p>
                      <p className="text-xs text-stone-500">{payload[0].payload.orders} orders</p>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Bar dataKey="revenue" fill="#D97706" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      );
    }
    
    // Default: Area chart
    return (
      <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={200}>
        <AreaChart data={filteredRevenueData}>
          <defs>
            <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#D97706" stopOpacity={0.3}/>
              <stop offset="95%" stopColor="#D97706" stopOpacity={0}/>
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
          <XAxis dataKey="day" stroke="#78716C" fontSize={12} tickLine={false} />
          <YAxis 
            stroke="#78716C" 
            fontSize={12}
            tickLine={false}
            tickFormatter={(value) => `₹${value >= 1000 ? `${(value/1000).toFixed(0)}k` : value}`}
          />
          <Tooltip 
            content={({ active, payload }) => {
              if (active && payload && payload.length) {
                return (
                  <div className="bg-white p-3 rounded-lg shadow-lg border border-stone-200">
                    <p className="text-sm font-medium text-stone-900">{payload[0].payload.date}</p>
                    <p className="text-lg font-bold text-amber-600">{formatCurrency(payload[0].value)}</p>
                    <p className="text-xs text-stone-500">{payload[0].payload.orders} orders</p>
                  </div>
                );
              }
              return null;
            }}
          />
          <Area 
            type="monotone" 
            dataKey="revenue" 
            stroke="#D97706" 
            strokeWidth={2}
            fill="url(#revenueGradient)" 
          />
        </AreaChart>
      </ResponsiveContainer>
    );
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 animate-pulse">
        {[...Array(8)].map((_, i) => (
          <div key={i} className={`bg-white rounded-xl h-44 ${i < 4 ? '' : 'lg:col-span-2'}`}></div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-stone-900" style={{ fontFamily: 'League Spartan, sans-serif' }}>
            Welcome back, {user?.name || user?.full_name || 'Admin'}!
          </h1>
          <p className="text-stone-500 mt-1">
            Here's what's happening with your business today
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm text-stone-500">
          <CalendarIcon className="w-4 h-4" />
          {new Date().toLocaleDateString('en-US', { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          })}
        </div>
      </div>

      {/* Pending Actions Alert */}
      {dashboardData.pendingActions.length > 0 && (
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-amber-800">Action Required</h3>
                <div className="mt-2 space-y-2">
                  {dashboardData.pendingActions.map((action, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <span className="text-amber-700">{action.description}</span>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="border-amber-600 text-amber-700 hover:bg-amber-100"
                        onClick={() => onNavigate(action.action)}
                      >
                        {action.title}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        {/* Today's Revenue */}
        <Card className="bg-white border-stone-200 shadow-sm rounded-xl hover:shadow-md transition-all duration-200 hover:-translate-y-1" data-testid="stat-today-revenue">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="p-3 bg-amber-100 rounded-xl">
                <IndianRupee className="w-6 h-6 text-amber-600" />
              </div>
              <Badge variant="outline" className="text-green-600 border-green-200 bg-green-50">
                <ArrowUpRight className="w-3 h-3 mr-1" />
                Today
              </Badge>
            </div>
            <div className="mt-4">
              <p className="text-sm text-stone-500">Today's Revenue</p>
              <p className="text-2xl sm:text-3xl font-bold text-stone-900" style={{ fontFamily: 'League Spartan, sans-serif' }}>
                {formatCurrency(dashboardData.todayStats.revenue)}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Today's Orders */}
        <Card className="bg-white border-stone-200 shadow-sm rounded-xl hover:shadow-md transition-all duration-200 hover:-translate-y-1" data-testid="stat-today-orders">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="p-3 bg-blue-100 rounded-xl">
                <ShoppingCart className="w-6 h-6 text-blue-600" />
              </div>
              <Badge variant="outline" className="text-blue-600 border-blue-200 bg-blue-50">
                <Activity className="w-3 h-3 mr-1" />
                Live
              </Badge>
            </div>
            <div className="mt-4">
              <p className="text-sm text-stone-500">Today's Orders</p>
              <p className="text-2xl sm:text-3xl font-bold text-stone-900" style={{ fontFamily: 'League Spartan, sans-serif' }}>
                {dashboardData.todayStats.orders}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Active Franchises */}
        <Card className="bg-white border-stone-200 shadow-sm rounded-xl hover:shadow-md transition-all duration-200 hover:-translate-y-1" data-testid="stat-franchises">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="p-3 bg-green-100 rounded-xl">
                <Store className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <div className="mt-4">
              <p className="text-sm text-stone-500">Active Franchises</p>
              <p className="text-2xl sm:text-3xl font-bold text-stone-900" style={{ fontFamily: 'League Spartan, sans-serif' }}>
                {franchises.length}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Avg Order Value */}
        <Card className="bg-white border-stone-200 shadow-sm rounded-xl hover:shadow-md transition-all duration-200 hover:-translate-y-1" data-testid="stat-avg-order">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="p-3 bg-purple-100 rounded-xl">
                <Target className="w-6 h-6 text-purple-600" />
              </div>
            </div>
            <div className="mt-4">
              <p className="text-sm text-stone-500">Avg. Order Value</p>
              <p className="text-2xl sm:text-3xl font-bold text-stone-900" style={{ fontFamily: 'League Spartan, sans-serif' }}>
                {formatCurrency(dashboardData.todayStats.avgOrderValue)}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Revenue Trend Chart */}
        <Card className="lg:col-span-2 bg-white border-stone-200 shadow-sm rounded-xl" data-testid="revenue-chart">
          <CardHeader className="pb-2">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div>
                <CardTitle className="text-lg font-semibold text-stone-900">Revenue Trend</CardTitle>
                <CardDescription>
                  {filteredRevenueTotals.orders} orders • {formatCurrency(filteredRevenueTotals.revenue)} total
                </CardDescription>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <ChartTypeSelector chartType={revenueChartType} onChartTypeChange={setRevenueChartType} />
                <DateRangePicker 
                  dateRange={revenueDateRange} 
                  onDateRangeChange={setRevenueDateRange}
                  label="Revenue Trend"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-[280px]" style={{ minHeight: '280px', minWidth: '200px' }}>
              {renderRevenueChart()}
            </div>
          </CardContent>
        </Card>

        {/* Top Items */}
        <Card className="bg-white border-stone-200 shadow-sm rounded-xl" data-testid="top-items">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg font-semibold text-stone-900">Top Selling Items</CardTitle>
                <CardDescription>Best performers</CardDescription>
              </div>
              <DateRangePicker 
                dateRange={topItemsDateRange} 
                onDateRangeChange={setTopItemsDateRange}
                label="Top Items"
              />
            </div>
          </CardHeader>
          <CardContent>
            {filteredTopItems.length > 0 ? (
              <div className="space-y-4">
                {filteredTopItems.map((item, idx) => (
                  <div key={`item-${item.name}-${idx}`} className="flex items-center gap-3">
                    <div 
                      className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-sm"
                      style={{ backgroundColor: CHART_COLORS[idx % CHART_COLORS.length] }}
                    >
                      {idx + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-stone-900 truncate">{item.name}</p>
                      <p className="text-sm text-stone-500">{item.count} sold</p>
                    </div>
                    <p className="font-semibold text-stone-900">{formatCurrency(item.revenue)}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-stone-500">
                <Coffee className="w-12 h-12 mx-auto mb-3 text-stone-300" />
                <p>No sales data for selected period</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Franchise Performance */}
        <Card className="bg-white border-stone-200 shadow-sm rounded-xl" data-testid="franchise-performance">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg font-semibold text-stone-900">Franchise Performance</CardTitle>
                <CardDescription>All locations ({filteredFranchisePerformance.length} total)</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <DateRangePicker 
                  dateRange={franchiseDateRange} 
                  onDateRangeChange={setFranchiseDateRange}
                  label="Franchise Performance"
                />
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-amber-600"
                  onClick={() => onNavigate('franchises')}
                >
                  Manage <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {filteredFranchisePerformance.length > 0 ? (
              <div className="space-y-4 max-h-[400px] overflow-y-auto">
                {filteredFranchisePerformance.map((franchise, idx) => (
                  <div key={franchise.id || idx} className="flex items-center gap-4 p-3 rounded-lg hover:bg-stone-50 transition-colors">
                    <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                      <Store className="w-5 h-5 text-amber-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-stone-900 truncate">{franchise.name}</p>
                      <p className="text-sm text-stone-500">{franchise.orders} orders</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-stone-900">{formatCurrency(franchise.revenue)}</p>
                      <div className={`flex items-center justify-end text-sm ${franchise.trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
                        {franchise.trend === 'up' ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
                        {franchise.trendValue}%
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-stone-500">
                <Store className="w-12 h-12 mx-auto mb-3 text-stone-300" />
                <p>No franchise data for selected period</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Orders */}
        <Card className="bg-white border-stone-200 shadow-sm rounded-xl" data-testid="recent-orders">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg font-semibold text-stone-900">Recent Orders</CardTitle>
                <CardDescription>Latest transactions</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <DateRangePicker 
                  dateRange={recentOrdersDateRange} 
                  onDateRangeChange={setRecentOrdersDateRange}
                  label="Recent Orders"
                />
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-amber-600"
                  onClick={() => onNavigate('reports')}
                >
                  View All <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {filteredRecentOrders.length > 0 ? (
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {filteredRecentOrders.map((order) => (
                  <div key={order.id} className="flex items-center gap-4 p-3 rounded-lg hover:bg-stone-50 transition-colors">
                    <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                      <ShoppingCart className="w-5 h-5 text-green-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-stone-900">#{order.id}</p>
                      <p className="text-sm text-stone-500 truncate">{order.franchise}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-stone-900">{formatCurrency(order.amount)}</p>
                      <p className="text-xs text-stone-500 flex items-center justify-end">
                        <Clock className="w-3 h-3 mr-1" />
                        {order.time}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-stone-500">
                <ShoppingCart className="w-12 h-12 mx-auto mb-3 text-stone-300" />
                <p>No orders for selected period</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card className="bg-gradient-to-r from-amber-600 to-amber-700 border-0 text-white rounded-xl" data-testid="quick-actions">
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h3 className="text-xl font-semibold">Quick Actions</h3>
              <p className="text-amber-100 mt-1">Jump to common tasks</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button 
                variant="secondary" 
                className="bg-white text-amber-700 hover:bg-amber-50"
                onClick={() => onNavigate('franchises')}
              >
                <Store className="w-4 h-4 mr-2" />
                Manage Franchises
              </Button>
              <Button 
                variant="secondary" 
                className="bg-white text-amber-700 hover:bg-amber-50"
                onClick={() => onNavigate('menu')}
              >
                <Coffee className="w-4 h-4 mr-2" />
                Edit Menu
              </Button>
              <Button 
                variant="secondary" 
                className="bg-white text-amber-700 hover:bg-amber-50"
                onClick={() => onNavigate('reports')}
              >
                <Wallet className="w-4 h-4 mr-2" />
                View Reports
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardOverview;
